import { useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Sparkles, ShieldCheck, FileText, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";
import { SectionShell } from "./SectionShell";

const SOURCES = ["", "Zoom", "Google Meet", "Phone", "In-person", "Other"];

export default function TranscriptTab({ interviewId, data, setData }) {
  const [transcriptText, setTranscriptText] = useState(data?.transcript_text || "");
  const [metadata, setMetadata] = useState({
    call_date: data?.transcript_metadata?.call_date || "",
    participants: data?.transcript_metadata?.participants || "",
    source: data?.transcript_metadata?.source || "",
  });
  const [consent, setConsent] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setMeta = (k, v) => setMetadata((p) => ({ ...p, [k]: v }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-picked after an error
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15MB).");
      return;
    }
    const lower = file.name.toLowerCase();
    // .txt / .md → read locally so we don't burn a round-trip
    if (lower.endsWith(".txt") || lower.endsWith(".md")) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setTranscriptText(String(ev.target?.result || ""));
        toast.success(`Loaded ${file.name} (${file.size.toLocaleString()} bytes)`);
      };
      reader.readAsText(file);
      return;
    }
    if (!lower.endsWith(".pdf") && !lower.endsWith(".docx")) {
      toast.error("Supported types: .pdf, .docx, .txt, .md");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/interviews/${interviewId}/transcript-upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      setTranscriptText(r.data.transcript_text || "");
      const truncNote = r.data.truncated ? " (truncated to 200k chars)" : "";
      toast.success(`Extracted ${r.data.char_count.toLocaleString()} chars from ${file.name}${truncNote}. Review and click Extract when ready.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that file. Try exporting as .txt.");
    } finally {
      setUploading(false);
    }
  };

  const runExtract = async () => {
    if (!consent) {
      toast.error("Please confirm consent before extracting.");
      return;
    }
    if (!transcriptText.trim()) {
      toast.error("Paste or upload a transcript first.");
      return;
    }
    setExtracting(true);
    try {
      const r = await api.post(`/interviews/${interviewId}/transcript-extract`, {
        transcript_text: transcriptText,
        transcript_metadata: metadata,
        consent_confirmed: true,
      });
      setData(r.data.interview);
      toast.success("Transcript extracted — review the structured intake on this page.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed. Please try again.");
    } finally {
      setExtracting(false);
    }
  };

  const extraction = data?.transcript_extraction;
  const hasExtraction = !!extraction;

  return (
    <SectionShell
      title="Transcript / Call Notes"
      eyebrow="Turn the conversation into structured intake"
    >
      <p className="text-sm text-navy/60 -mt-6 mb-6">
        Paste a call transcript or upload the notes (.pdf, .docx, .txt). Goldline will extract the business model, friction points, lead flow, operations gaps, automation opportunities, and recommended service path.
      </p>
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="t-date">Call date</Label>
            <Input id="t-date" type="date" data-testid="transcript-date" value={metadata.call_date}
              onChange={(e) => setMeta("call_date", e.target.value)} className="mt-2 rounded-none bg-white" />
          </div>
          <div>
            <Label htmlFor="t-people">Participants</Label>
            <Input id="t-people" data-testid="transcript-participants" value={metadata.participants}
              onChange={(e) => setMeta("participants", e.target.value)}
              placeholder="e.g. Christian, Sarah Chen, Mike (CFO)"
              className="mt-2 rounded-none bg-white" />
          </div>
          <div>
            <Label htmlFor="t-src">Source</Label>
            <select
              id="t-src"
              data-testid="transcript-source"
              value={metadata.source}
              onChange={(e) => setMeta("source", e.target.value)}
              className="mt-2 w-full h-10 px-3 border border-navy/15 rounded-none bg-white text-sm"
            >
              {SOURCES.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Label htmlFor="t-text">Transcript text</Label>
            <label
              className={`cursor-pointer text-xs inline-flex items-center gap-1 hover:underline ${uploading ? "text-navy/40 pointer-events-none" : "text-gold"}`}
              data-testid="transcript-upload-label"
            >
              {uploading ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Reading file…</>
              ) : (
                <><Upload className="w-3 h-3" /> Upload .pdf / .docx / .txt</>
              )}
              <input
                type="file"
                accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="transcript-upload-input"
                disabled={uploading}
              />
            </label>
          </div>
          <textarea
            id="t-text"
            data-testid="transcript-text"
            value={transcriptText}
            onChange={(e) => setTranscriptText(e.target.value)}
            placeholder="Paste the call transcript here…"
            rows={14}
            className="w-full p-3 border border-navy/15 rounded-none bg-white text-sm font-mono leading-relaxed"
          />
          <div className="text-xs text-navy/40 mt-1">{transcriptText.length.toLocaleString()} characters</div>
        </div>

        <label className="flex items-start gap-3 p-4 border border-navy/15 bg-white cursor-pointer" data-testid="transcript-consent-label">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 w-4 h-4 accent-gold"
            data-testid="transcript-consent"
          />
          <div className="text-xs text-navy/80 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-gold" />
            I confirm I have permission to upload or paste this transcript and understand it may contain confidential business information.
          </div>
        </label>

        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={runExtract}
            disabled={extracting || !consent || !transcriptText.trim()}
            className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold"
            data-testid="transcript-extract-btn"
          >
            {extracting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Extract Intake Details</>
            )}
          </Button>
          {hasExtraction && (
            <span className="text-xs text-navy/50 self-center">
              Last extraction: {extraction?._extracted_at ? new Date(extraction._extracted_at).toLocaleString() : "saved"}
            </span>
          )}
        </div>

        {hasExtraction && (
          <ExtractedPreview extraction={extraction} />
        )}

        <div className="mt-4 text-[11px] text-navy/40 italic flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          Generated outputs are draft consulting materials and should be reviewed by Goldline before being shared with clients.
        </div>
      </div>
    </SectionShell>
  );
}

function ExtractedPreview({ extraction }) {
  const cb = extraction?.client_basics || {};
  const wn = extraction?.why_now || {};
  const cf = extraction?.current_friction || {};
  const lgs = extraction?.lead_generation_and_sales || {};
  const rec = extraction?.recommended_goldline_offer || {};

  return (
    <div className="bg-ivory border border-gold p-6 space-y-5" data-testid="extracted-preview">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold font-semibold">
        <FileText className="w-3.5 h-3.5" /> Structured intake extracted
      </div>

      <PreviewBlock label="Business basics">
        <div className="grid md:grid-cols-3 gap-x-4 gap-y-1 text-sm text-navy/80">
          <KV k="Business" v={cb.business_name} />
          <KV k="Industry" v={cb.industry} />
          <KV k="Stage" v={cb.business_stage} />
          <KV k="Team" v={cb.team_size} />
          <KV k="Revenue" v={cb.revenue_range} />
          <KV k="Location" v={cb.location} />
        </div>
      </PreviewBlock>

      {(wn.trigger_event || wn.urgency) && (
        <PreviewBlock label="Why now">
          <div className="text-sm text-navy/80 space-y-1">
            <p><strong>Trigger:</strong> {wn.trigger_event || "—"}</p>
            <p><strong>Urgency:</strong> {wn.urgency || "—"}</p>
            {wn.cost_of_doing_nothing && <p><strong>Cost of doing nothing:</strong> {wn.cost_of_doing_nothing}</p>}
          </div>
        </PreviewBlock>
      )}

      {cf.summary && (
        <PreviewBlock label="Current friction">
          <p className="text-sm text-navy/80">{cf.summary}</p>
          {!!(cf.time_leaks || []).length && (
            <ListChips title="Time leaks" items={cf.time_leaks} />
          )}
          {!!(cf.money_leaks || []).length && (
            <ListChips title="Money leaks" items={cf.money_leaks} />
          )}
        </PreviewBlock>
      )}

      {(lgs.lead_sources || lgs.crm_or_pipeline) && (
        <PreviewBlock label="Lead flow & sales">
          <div className="text-sm text-navy/80 space-y-1">
            {lgs.lead_sources && <p><strong>Sources:</strong> {lgs.lead_sources}</p>}
            {lgs.speed_to_lead && <p><strong>Speed-to-lead:</strong> {lgs.speed_to_lead}</p>}
            {lgs.crm_or_pipeline && <p><strong>CRM:</strong> {lgs.crm_or_pipeline}</p>}
            {lgs.dropped_leads && <p><strong>Dropped leads:</strong> {lgs.dropped_leads}</p>}
          </div>
        </PreviewBlock>
      )}

      {rec.primary_service && (
        <PreviewBlock label="Transcript extractor suggested">
          <div className="text-sm text-navy/80 space-y-1">
            <p><strong>Primary:</strong> <span className="text-gold font-semibold">{rec.primary_service}</span></p>
            {rec.secondary_service && <p><strong>Secondary:</strong> {rec.secondary_service}</p>}
            {rec.reasoning && <p className="text-navy/60">{rec.reasoning}</p>}
            <p className="text-xs text-navy/40">Urgency: {rec.urgency || "—"} · Confidence: {rec.confidence || "—"}</p>
          </div>
        </PreviewBlock>
      )}

      {!!(extraction.missing_information || []).length && (
        <PreviewBlock label="Missing information">
          <ul className="list-disc list-inside text-sm text-navy/70 space-y-1">
            {extraction.missing_information.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </PreviewBlock>
      )}

      {!!(extraction.follow_up_questions || []).length && (
        <PreviewBlock label="Follow-up questions">
          <ul className="list-disc list-inside text-sm text-navy/70 space-y-1">
            {extraction.follow_up_questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </PreviewBlock>
      )}
    </div>
  );
}

function PreviewBlock({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold mb-2">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  if (!v || v === "Not provided") return null;
  return <div><span className="text-navy/40">{k}:</span> {v}</div>;
}

function ListChips({ title, items }) {
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span key={i} className="inline-block text-xs bg-white border border-navy/15 px-2 py-1">{it}</span>
        ))}
      </div>
    </div>
  );
}
